import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { getApiErrorMessage } from '../api';
import { useParticipantTheme } from '../utils/theme';
import ShareCard from './presentations/ShareCard';
import SvodxLogo from './brand/SvodxLogo';
import HeatmapPicker from './heatmap/HeatmapPicker';
import { markJustVoted } from './presentations/shareUtils';
import './PollVote.css';

const PollVote = () => {
    const { pollId } = useParams();
    const navigate = useNavigate();
    const [poll, setPoll] = useState(null);
    useParticipantTheme(poll?.theme);
    const [loading, setLoading] = useState(true);
    const [answers, setAnswers] = useState({});
    const [otherTexts, setOtherTexts] = useState({}); // свой ответ в «Другое»: { [questionId]: текст }
    const [studentId, setStudentId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [voted, setVoted] = useState(false);
    // Пошаговое прохождение: один вопрос на экране
    const [step, setStep] = useState(0);
    const [stepError, setStepError] = useState(null);
    const questionRef = useRef(null);

    useEffect(() => {
        // Стабильный номер гостя в этом браузере. Отрицательный — чтобы не пересекаться с Telegram ID,
        // и из огромного диапазона — чтобы у разных участников номера не совпадали
        let numericId = null;
        try {
            numericId = parseInt(localStorage.getItem('guest_user_id'), 10);
        } catch {
            numericId = null;
        }
        if (!Number.isSafeInteger(numericId) || numericId >= 0) {
            numericId = -(Math.floor(Math.random() * (2 ** 52)) + 1);
            try {
                localStorage.setItem('guest_user_id', String(numericId));
            } catch {
                // без localStorage номер живёт до закрытия вкладки
            }
        }
        setStudentId(numericId);

        const loadPoll = async () => {
            try {
                if (/^\d+$/.test(String(pollId))) {
                    const legacyRes = await api.get(`/polls/${pollId}`);
                    const canonicalRef = legacyRes.data.public_id;
                    if (canonicalRef && canonicalRef !== pollId) {
                        navigate(`/vote/${canonicalRef}`, { replace: true });
                        return;
                    }
                    setPoll(legacyRes.data);
                    return;
                }

                const res = await api.get(`/public/polls/${pollId}`);
                setPoll(res.data);
            } catch (err) {
                console.error("Failed to fetch poll", err);
                alert("Опрос не найден или ошибка загрузки");
            } finally {
                setLoading(false);
            }
        };

        loadPoll();
    }, [pollId, navigate]);

    // Проверка, не голосовал ли уже этот пользователь
    useEffect(() => {
        if (pollId && studentId) {
            api.get(`/public/polls/${pollId}/check-answered/${studentId}`)
                .then(res => {
                    if (res.data.answered) {
                        setVoted(true);
                    }
                })
                .catch(err => {
                    console.error("Error checking vote status", err);
                });
        }
    }, [pollId, studentId]);

    const handleOptionSelect = (questionId, optionId, type, allowMultiple) => {
        setAnswers(prev => {
            const currentQuestionAnswers = prev[questionId] || [];

            if (type === 'ranking') {
                return prev;
            }

            if (type === 'grid' || type === 'single' || (!type && !allowMultiple)) {
                return { ...prev, [questionId]: [optionId] };
            }

            if (type === 'multiple' || (!type && allowMultiple)) {
                if (currentQuestionAnswers.includes(optionId)) {
                    return { ...prev, [questionId]: currentQuestionAnswers.filter(id => id !== optionId) };
                } else {
                    return { ...prev, [questionId]: [...currentQuestionAnswers, optionId] };
                }
            }

            return prev;
        });
    };

    const handleRankingAdd = (questionId, optionId, optionsCount) => {
        setAnswers(prev => {
            const cur = [...(prev[questionId] || [])];
            if (cur.includes(optionId) || cur.length >= optionsCount) return prev;
            return { ...prev, [questionId]: [...cur, optionId] };
        });
    };

    const handleRankingRemoveAt = (questionId, index) => {
        setAnswers(prev => {
            const cur = [...(prev[questionId] || [])];
            cur.splice(index, 1);
            return { ...prev, [questionId]: cur };
        });
    };

    const handleRankingReset = (questionId) => {
        setAnswers(prev => ({ ...prev, [questionId]: [] }));
    };

    const handleNumberChange = (questionId, value) => {
        const num = value === '' ? undefined : parseFloat(value);
        setAnswers(prev => ({ ...prev, [questionId]: num }));
    };

    const handleTextChange = (questionId, value) => {
        setAnswers(prev => ({ ...prev, [questionId]: value }));
    };

    const getWordcloudHint = (question) => {
        const minLen = question.wordcloud_min_len ?? 2;
        const maxLen = question.wordcloud_max_len ?? 30;
        const maxWords = question.wordcloud_max_words_per_response ?? 10;
        if (question.wordcloud_mode !== 'words') {
            return 'Напишите коротко — словом или фразой. Одинаковые ответы в облаке объединятся и станут крупнее.';
        }
        return `Разрешено: от ${minLen} до ${maxLen} символов в слове, максимум ${maxWords} слов в ответе.`;
    };

    const answerFor = (q) => answers[q.id] ?? answers[String(q.id)];

    const otherOptionOf = (q) => (q.options || []).find((o) => o.is_other);
    const otherSelected = (q) => {
        const other = otherOptionOf(q);
        const val = answerFor(q);
        return Boolean(other && Array.isArray(val) && val.includes(other.id));
    };

    // Почему на вопрос нельзя уйти дальше (null — можно)
    const stepProblem = (q) => {
        const val = answerFor(q);
        if (q.type === 'ranking') {
            const ranked = Array.isArray(val) ? val.length : 0;
            const total = (q.options || []).length;
            if (ranked > 0 && ranked < total) return 'Расставьте по местам все варианты или сбросьте порядок.';
            if (q.required && ranked === 0) return 'Это обязательный вопрос. Расставьте варианты по местам, чтобы продолжить.';
            return null;
        }
        if (otherSelected(q)) {
            const text = (otherTexts[q.id] || '').trim();
            if (!text) return `Напишите свой вариант в поле под «${otherOptionOf(q).text}» или выберите другой ответ.`;
            if (text.length > OTHER_MAX_LEN) return `Свой вариант — не длиннее ${OTHER_MAX_LEN} символов.`;
        }
        if (!q.required) return null;
        let answered;
        if (q.type === 'number' || q.type === 'rating') answered = val != null && !Number.isNaN(val);
        else if (q.type === 'wordcloud') answered = Boolean((val || '').trim());
        else answered = Array.isArray(val) ? val.length > 0 : val != null;
        if (answered) return null;
        if (q.type === 'heatmap') return 'Это обязательный вопрос. Отметьте место на картинке, чтобы продолжить.';
        return q.type === 'wordcloud' || q.type === 'number'
            ? 'Это обязательный вопрос. Введите ответ, чтобы продолжить.'
            : 'Это обязательный вопрос. Выберите ответ, чтобы продолжить.';
    };

    const hasAnyAnswer = Object.values(answers).some((v) => (
        Array.isArray(v) ? v.length > 0 : typeof v === 'string' ? v.trim() !== '' : v != null && !Number.isNaN(v)
    ));

    // Начатые, но не отправленные ответы: браузер спросит перед закрытием или уходом со страницы
    useEffect(() => {
        if (!hasAnyAnswer || voted || submitting) return undefined;
        const onBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = 'Ответы ещё не отправлены. Уйти со страницы?';
            return e.returnValue;
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [hasAnyAnswer, voted, submitting]);

    // Ответ поменялся — сообщение об ошибке больше не актуально
    useEffect(() => { setStepError(null); }, [answers, otherTexts]);

    // При переходе между вопросами показываем начало вопроса (при первом открытии — не прокручиваем)
    const stepShownRef = useRef(false);
    useEffect(() => {
        if (!stepShownRef.current) {
            stepShownRef.current = true;
            return;
        }
        if (questionRef.current) questionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [step]);

    const goNext = () => {
        const q = poll.questions[step];
        const problem = stepProblem(q);
        if (problem) {
            setStepError(problem);
            return false;
        }
        setStepError(null);
        setStep((s) => Math.min(s + 1, poll.questions.length - 1));
        return true;
    };

    const goBack = () => {
        setStepError(null);
        setStep((s) => Math.max(s - 1, 0));
    };

    const trySubmit = () => {
        // Проверяем все вопросы: при ошибке показываем первый проблемный
        const idx = poll.questions.findIndex((q) => stepProblem(q));
        if (idx !== -1) {
            setStep(idx);
            setStepError(stepProblem(poll.questions[idx]));
            return;
        }
        handleSubmit();
    };

    const handleSubmit = async () => {
        if (!studentId) return;

        setSubmitting(true);
        try {
            const formattedAnswers = [];
            poll.questions.forEach(q => {
                const qId = String(q.id);
                const val = answers[q.id] ?? answers[qId];
                if (q.type === 'number' || q.type === 'rating') {
                    if (val != null && !Number.isNaN(val))
                        formattedAnswers.push({ question_id: parseInt(qId), numeric_value: val });
                } else if (q.type === 'heatmap') {
                    (Array.isArray(val) ? val : []).forEach((p) => {
                        formattedAnswers.push({ question_id: parseInt(qId), point_x: p.x, point_y: p.y });
                    });
                } else if (q.type === 'wordcloud') {
                    const text = (val || '').trim();
                    if (text) {
                        formattedAnswers.push({ question_id: parseInt(qId), text_value: text });
                    }
                } else if (q.type === 'ranking') {
                    const rankedIds = Array.isArray(val) ? val : [];
                    const nOpts = (q.options || []).length;
                    // Пустой порядок — вопрос пропущен (обязательность проверена до отправки); частичный — ошибка
                    if (nOpts > 0 && rankedIds.length > 0 && rankedIds.length !== nOpts) {
                        throw new Error(`Ранжирование: укажите порядок для всех вариантов в вопросе «${(q.text || '').slice(0, 80)}${(q.text || '').length > 80 ? '…' : ''}».`);
                    }
                    rankedIds.forEach((optId, i) => {
                        formattedAnswers.push({
                            question_id: parseInt(qId),
                            option_id: optId,
                            rank_value: i + 1,
                        });
                    });
                } else {
                    const qAnswers = Array.isArray(val) ? val : (val != null ? [val] : []);
                    const other = otherOptionOf(q);
                    qAnswers.forEach(optId => {
                        const item = { question_id: parseInt(qId), option_id: optId };
                        if (other && optId === other.id) item.text_value = (otherTexts[q.id] || '').trim();
                        formattedAnswers.push(item);
                    });
                }
            });

            await api.post('/public/responses', {
                poll_public_id: pollId,
                student_telegram_id: studentId,
                answers: formattedAnswers
            });

            if (poll.show_results === false) {
                setVoted(true);
            } else {
                // Карточку «Получить презентацию» на странице результатов увидит только тот, кто ответил
                if (poll.share?.enabled) markJustVoted(pollId);
                navigate(`/poll/${pollId}`);
            }
        } catch (err) {
            console.error(err);
            if (err instanceof Error && err.message.startsWith('Ранжирование:')) {
                alert(err.message);
            } else {
                const detail = getApiErrorMessage(err, err.message || 'Ошибка при отправке ответа');
                alert("Ошибка при отправке: " + detail);
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="vote-loading">Загрузка...</div>;
    if (!poll) return <div className="vote-loading">Опрос не найден</div>;

    if (!poll.is_active) {
        return (
            <div className="vote-page">
                <div className="vote-error-card">
                    {poll.archived_at ? (
                        <>
                            <h2>Опрос завершён</h2>
                            <p>Голосование закончилось, ответы больше не принимаются. Спасибо за интерес!</p>
                        </>
                    ) : (
                        <>
                            <h2>Опрос не активен</h2>
                            <p>В данный момент этот опрос закрыт организатором. Пожалуйста, дождитесь активации или обратитесь к организатору.</p>
                        </>
                    )}
                    <button className="btn btn--secondary" onClick={() => { window.location.href = '/'; }}>На главную</button>
                </div>
            </div>
        );
    }

    if (!poll.questions || poll.questions.length === 0) {
        return (
            <div className="vote-page">
                <div className="vote-error-card">
                    <h2>В опросе пока нет вопросов</h2>
                    <p>Организатор ещё не добавил вопросы в этот опрос. Попробуйте открыть ссылку позже или обратитесь к организатору.</p>
                </div>
            </div>
        );
    }

    if (voted) {
        return (
            <div className="vote-page">
                <div className="vote-error-card" style={{ textAlign: 'center', marginTop: '10vh' }}>
                    <h2>Голос принят!</h2>
                    <p>Спасибо за участие.{poll.share?.enabled ? '' : ' Страницу можно закрыть.'}</p>
                </div>
                {poll.share?.enabled && (
                    <div style={{ maxWidth: 520, margin: '20px auto 0' }}>
                        <ShareCard kind="poll" refId={poll.public_id || pollId} share={poll.share} dismissible />
                    </div>
                )}
            </div>
        );
    }

    const centered = !!poll.company_logo_path;

    return (
        <div className="vote-page">
            {poll.company_logo_path && (
                <div className="vote-logo-wrap">
                    <img 
                      src={`${api.defaults.baseURL ? api.defaults.baseURL.replace('/api', '') : ''}/${poll.company_logo_path}`} 
                      alt="Company Logo" 
                      className="vote-logo"
                    />
                </div>
            )}
            <h1 className={`vote-title ${centered ? 'vote-title--center' : ''}`}>{poll.title}</h1>
            <p className={`vote-desc ${centered ? 'vote-desc--center' : ''}`}>{poll.description}</p>

            <div className="vote-progress" aria-label={`Вопрос ${step + 1} из ${poll.questions.length}`}>
                <div className="vote-progress__text">
                    <span>Вопрос {step + 1} из {poll.questions.length}</span>
                    {poll.questions.some((q) => q.required) && (
                        <span className="vote-progress__legend"><span className="vote-required-mark">*</span> — обязательный вопрос</span>
                    )}
                </div>
                <div className="vote-progress__bar">
                    <i style={{ width: `${((step + 1) / poll.questions.length) * 100}%` }} />
                </div>
            </div>

            {poll.questions.map((q, idx) => idx === step && (
                <div key={q.id} ref={questionRef} className={`vote-question${stepError ? ' vote-question--error' : ''}`}>
                    <h3>
                        {idx + 1}. {q.text}
                        {q.required && <span className="vote-required-mark" title="Обязательный вопрос" aria-label="обязательный вопрос"> *</span>}
                    </h3>

                    {q.image_id && (
                        <div className="vote-image-wrap">
                            {q.type === 'heatmap' ? (
                                <HeatmapPicker
                                    image={q.image_id}
                                    max={q.heatmap_max_clicks || 1}
                                    value={answers[q.id] || []}
                                    onChange={(points) => setAnswers((prev) => ({ ...prev, [q.id]: points }))}
                                />
                            ) : q.type === 'grid' ? (
                                <GridSelector
                                    image={q.image_id}
                                    rows={q.grid_rows || 3}
                                    cols={q.grid_cols || 3}
                                    options={q.options}
                                    selected={answers[q.id] || []}
                                    onSelect={(optId) => handleOptionSelect(q.id, optId, 'grid')}
                                />
                            ) : (
                                <img
                                    src={q.image_id.startsWith('http') || q.image_id.startsWith('/static') ? q.image_id : `/proxy/image/${q.image_id}`}
                                    alt="Question"
                                    className="vote-image"
                                />
                            )}
                        </div>
                    )}

                    {q.type === 'number' && (
                        <div style={{ marginTop: 'var(--space-3)' }}>
                            <input
                                type="number"
                                step="any"
                                min={q.min_value}
                                max={q.max_value}
                                value={answers[q.id] ?? ''}
                                onChange={(e) => handleNumberChange(q.id, e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (idx < poll.questions.length - 1) goNext(); else trySubmit(); } }}
                                placeholder={q.min_value != null && q.max_value != null ? `От ${q.min_value} до ${q.max_value}` : 'Введите число'}
                                className="vote-number-input"
                            />
                        </div>
                    )}

                    {q.type === 'rating' && (
                        <div className="vote-rating">
                            {Array.from({ length: q.max_value || 5 }).map((_, idx) => {
                                const starValue = idx + 1;
                                const isSelected = answers[q.id] && starValue <= answers[q.id];
                                return (
                                    <span
                                        key={starValue}
                                        role="button"
                                        tabIndex={0}
                                        className={`vote-rating-star ${isSelected ? 'vote-rating-star--selected' : ''}`}
                                        onClick={() => handleNumberChange(q.id, starValue)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNumberChange(q.id, starValue); } }}
                                        title={`Оценить на ${starValue}`}
                                    >
                                        ★
                                    </span>
                                );
                            })}
                        </div>
                    )}
                    {q.type === 'wordcloud' && (
                        <div style={{ marginTop: 'var(--space-3)' }}>
                            <textarea
                                value={answers[q.id] ?? ''}
                                onChange={(e) => handleTextChange(q.id, e.target.value)}
                                placeholder={getWordcloudHint(q)}
                                className="vote-number-input"
                                rows={3}
                                maxLength={280}
                            />
                            <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '6px' }}>
                                Ответ может попасть в результаты после премодерации.
                            </div>
                        </div>
                    )}
                    {q.type === 'ranking' && q.options && q.options.length > 0 && (
                        <div className="vote-ranking">
                            <p className="vote-ranking__hint">
                                Нажимайте варианты по порядку: первый выбор — 1-е место, затем 2-е и т.д. Можно снять место крестиком или сбросить весь порядок.
                            </p>
                            <div className="vote-ranking__block">
                                <div className="vote-ranking__label">Текущий порядок</div>
                                {(answers[q.id] || []).length === 0 ? (
                                    <div className="vote-ranking__empty">Пока ничего не выбрано</div>
                                ) : (
                                    <ol className="vote-ranking__ordered">
                                        {(answers[q.id] || []).map((optId, rankIdx) => {
                                            const opt = q.options.find(o => o.id === optId);
                                            if (!opt) return null;
                                            return (
                                                <li key={`${optId}-${rankIdx}`} className="vote-ranking__row">
                                                    <span className="vote-ranking__badge">{rankIdx + 1}</span>
                                                    <span className="vote-ranking__row-text">{opt.text}</span>
                                                    <button
                                                        type="button"
                                                        className="vote-ranking__remove"
                                                        onClick={() => handleRankingRemoveAt(q.id, rankIdx)}
                                                        aria-label={`Убрать с ${rankIdx + 1}-го места`}
                                                    >
                                                        ×
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ol>
                                )}
                                <button
                                    type="button"
                                    className="vote-ranking__reset"
                                    onClick={() => handleRankingReset(q.id)}
                                    disabled={!(answers[q.id] || []).length}
                                >
                                    Сбросить порядок
                                </button>
                            </div>
                            <div className="vote-ranking__block">
                                <div className="vote-ranking__label">
                                    {(answers[q.id] || []).length < q.options.length
                                        ? `Выберите ${(answers[q.id] || []).length + 1}-е место`
                                        : 'Все места расставлены'}
                                </div>
                                <div className="vote-options vote-options--ranking-pick">
                                    {q.options
                                        .filter(opt => !(answers[q.id] || []).includes(opt.id))
                                        .map(opt => (
                                            <div
                                                key={opt.id}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => handleRankingAdd(q.id, opt.id, q.options.length)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        handleRankingAdd(q.id, opt.id, q.options.length);
                                                    }
                                                }}
                                                className="vote-option"
                                            >
                                                <div className="vote-option__text">{opt.text}</div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </div>
                    )}
                    {!['grid', 'number', 'rating', 'wordcloud', 'ranking', 'heatmap'].includes(q.type) && q.options && (
                        <div className="vote-options">
                            {q.options.map(opt => {
                                const isSelected = (answers[q.id] || []).includes(opt.id);
                                const mult = q.type === 'multiple' || q.allow_multiple;
                                const optionRow = (
                                    <div
                                        key={opt.id}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => handleOptionSelect(q.id, opt.id, q.type, q.allow_multiple)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOptionSelect(q.id, opt.id, q.type, q.allow_multiple); } }}
                                        className={`vote-option ${isSelected ? 'vote-option--selected' : ''}`}
                                    >
                                        <div className={`vote-option__radio ${mult ? 'vote-option__radio--square' : ''} ${isSelected ? 'vote-option__radio--on' : ''}`}>
                                            {isSelected && <span style={{ fontSize: '12px' }}>✓</span>}
                                        </div>
                                        <div className="vote-option__text">
                                            {opt.text}
                                        </div>
                                    </div>
                                );
                                if (!opt.is_other) return optionRow;
                                const text = otherTexts[q.id] || '';
                                return (
                                    <React.Fragment key={opt.id}>
                                        {optionRow}
                                        {isSelected && (
                                            <div className="vote-other">
                                                <input
                                                    type="text"
                                                    className="vote-other__input"
                                                    value={text}
                                                    maxLength={OTHER_MAX_LEN}
                                                    autoFocus
                                                    placeholder="Напишите свой вариант"
                                                    aria-label={`Свой вариант для «${opt.text}»`}
                                                    onChange={(e) => setOtherTexts((prev) => ({ ...prev, [q.id]: e.target.value }))}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                                                />
                                                <div className="vote-other__meta">
                                                    <span>Ответ появится на результатах после автопроверки</span>
                                                    <span>{text.length}/{OTHER_MAX_LEN}</span>
                                                </div>
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    )}

                    {stepError && (
                        <div className="vote-step-error" role="alert">{stepError}</div>
                    )}
                </div>
            ))}

            <div className="vote-nav">
                {step > 0 && (
                    <button type="button" className="btn btn--secondary vote-nav__back" onClick={goBack} disabled={submitting}>
                        ← Назад
                    </button>
                )}
                {step < poll.questions.length - 1 ? (
                    <button type="button" className="btn btn--primary vote-nav__next" onClick={goNext}>
                        Далее →
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={trySubmit}
                        disabled={submitting}
                        className="btn btn--primary vote-submit vote-nav__next"
                    >
                        {submitting ? 'Отправка...' : 'Отправить ответы'}
                    </button>
                )}
            </div>
            {/* Корпоративный тариф: на странице только логотип организации */}
            {!poll.company_logo_path && (
                <a className="vote-powered" href="https://svodx.ru" target="_blank" rel="noopener noreferrer">
                    Работает на <SvodxLogo />
                </a>
            )}
        </div>
    );
};

const OTHER_MAX_LEN = 100;

const GridSelector = ({ image, rows, cols, options, selected, onSelect }) => {
    const sortedOptions = [...options].sort((a, b) => (parseInt(a.text) || 0) - (parseInt(b.text) || 0));

    return (
        <div className="vote-grid-wrap">
            <img
                src={image.startsWith('http') || image.startsWith('/static') ? image : `/proxy/image/${image}`}
                alt="Vote Grid"
                className="vote-grid-img"
            />
            <div
                className="vote-grid-overlay"
                style={{
                    gridTemplateRows: `repeat(${rows}, 1fr)`,
                    gridTemplateColumns: `repeat(${cols}, 1fr)`,
                }}
            >
                {Array.from({ length: rows * cols }).map((_, idx) => {
                    const option = sortedOptions[idx];
                    const isSelected = option && selected.includes(option.id);
                    const label = idx + 1;

                    return (
                        <div
                            key={option ? option.id : idx}
                            role="button"
                            tabIndex={option ? 0 : -1}
                            onClick={() => option && onSelect(option.id)}
                            onKeyDown={(e) => { if (option && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onSelect(option.id); } }}
                            className={`vote-grid-cell ${isSelected ? 'vote-grid-cell--selected' : ''}`}
                            style={{ cursor: option ? 'pointer' : 'default' }}
                            onMouseEnter={(e) => {
                                if (!option) return;
                                if (!isSelected) e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                            }}
                            onMouseLeave={(e) => {
                                if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            {isSelected && <span className="vote-grid-check">✓</span>}
                            <span className="vote-grid-idx">{label}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default PollVote;
